import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Eye, Key, Plus, Trash } from 'lucide-react';
import api from '../../utils/api';
import { PasswordResetModal } from './PasswordResetModal';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '../../components/Skeleton';
import AddVehicleModal from './AddVehicleModal';
import DeleteVehicleModal from './DeleteVehicleModal';

interface Company {
  id: number;
  raison_social: string;
  email: string;
  phone: string;
  address: string;
  ice: string;
  rib: string;
  client_type: string;
  latitude: number;
  longitude: number;
  avatar: string | null;
}

interface Client {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

interface Vehicle {
  id: number;
  plate_number: string;
  brand_name: string;
  model: string;
  year: string;
  fuel_type: string;
}

interface ServiceOrder {
  id: number;
  status: string;
  start_date: string;
  end_date: string;
  final_price: number;
  scheduled_at: string;
  completed_at: string;
  cancelled_at: string;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  perPage: number;
  total: number;
}

export default function CompanyDetails() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [company, setCompany] = useState<Company | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [serviceOrders, setServiceOrders] = useState<ServiceOrder[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('clients');
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);
  const [isAddVehicleModalOpen, setIsAddVehicleModalOpen] = useState(false);
  
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);

  const handleDeleteClick = (vehicle) => {
    setVehicleToDelete(vehicle);
    setIsDeleteModalOpen(true);
  };



  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setVehicleToDelete(null);
  };

  const navigate = useNavigate();
  const [clientPagination, setClientPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    perPage: 15,
    total: 0
  });
  const [serviceOrderPagination, setServiceOrderPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    perPage: 15,
    total: 0
  });
  const [vehiclePagination, setVehiclePagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    perPage: 15,
    total: 0
  });
  const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] = useState(false);
  const [passwordResetTarget, setPasswordResetTarget] = useState<{ type: 'client' | 'vehicle', id: number } | null>(null);

  useEffect(() => {
    loadCompanyDetails();
  }, [id]);

  useEffect(() => {
    if (company?.latitude && company?.longitude && mapContainer.current) {
      if (map.current) return;

      if (mapContainer.current.firstChild) {
        mapContainer.current.innerHTML = '';
      }

      if (company.latitude === null || company.longitude === null) {
        const warningElement = document.createElement('div');
        warningElement.className = 'bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4';
        warningElement.innerHTML = `
          <div class="flex items-center">
            <p>${t('no_geodata_available')}</p>
          </div>
        `;
        mapContainer.current.appendChild(warningElement);
        return;
      }

      try {
        mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

        map.current = new mapboxgl.Map({
          container: mapContainer.current!,
          style: 'mapbox://styles/mapbox/streets-v11',
          center: [company.longitude, company.latitude],
          zoom: 13
        });

        marker.current = new mapboxgl.Marker()
          .setLngLat([company.longitude, company.latitude])
          .addTo(map.current);

      } catch (error) {
        console.error('Error initializing map:', error);
        const errorElement = document.createElement('div');
        errorElement.className = 'bg-red-100 border-l-4 border-red-500 text-red-700 p-4';
        errorElement.innerHTML = `
          <div class="flex items-center">
            <p>${t('map_initialization_error')}</p>
          </div>
        `;
        mapContainer.current.appendChild(errorElement);
      }
    }
  }, [company?.latitude, company?.longitude, mapContainer, t]);

  const loadCompanyDetails = async () => {
    setLoading(true);
    try {
      const companyResponse = await api.get(`/companies/${id}`);
      setCompany(companyResponse.data);
      await Promise.all([
        loadClients(),
        loadServiceOrders(),
        loadVehicles()
      ]);
    } catch (error) {
      console.error('Error loading company details:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async (page = 1) => {
    try {
      const response = await api.get(`/companies/${id}/clients`, {
        params: {
          page,
          per_page: clientPagination.perPage
        }
      });
      setClients(response.data.data);
      setClientPagination({
        currentPage: response.data.current_page,
        totalPages: response.data.last_page,
        perPage: response.data.per_page,
        total: response.data.total
      });
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadServiceOrders = async (page = 1) => {
    try {
      const response = await api.get(`/companies/${id}/service-orders`, {
        params: {
          page,
          per_page: serviceOrderPagination.perPage
        }
      });
      setServiceOrders(response.data.data);
      setServiceOrderPagination({
        currentPage: response.data.current_page,
        totalPages: response.data.last_page,
        perPage: response.data.per_page,
        total: response.data.total
      });
    } catch (error) {
      console.error('Error loading service orders:', error);
    }
  };

  const loadVehicles = async (page = 1) => {
    try {
      const response = await api.get(`/companies/${id}/vehicles`, {
        params: {
          page,
          per_page: vehiclePagination.perPage
        }
      });
      setVehicles(response.data.data);
      setVehiclePagination({
        currentPage: response.data.current_page,
        totalPages: response.data.last_page,
        perPage: response.data.per_page,
        total: response.data.total
      });
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  };

  const handlePageChange = (newPage: number, type: 'clients' | 'serviceOrders' | 'vehicles') => {
    switch (type) {
      case 'clients':
        loadClients(newPage);
        break;
      case 'serviceOrders':
        loadServiceOrders(newPage);
        break;
      case 'vehicles':
        loadVehicles(newPage);
        break;
    }
  };

  const handleResetPassword = (type: 'client' | 'vehicle', id: number) => {
    setPasswordResetTarget({ type, id });
    setIsPasswordResetModalOpen(true);
  };

  const handlePasswordResetConfirm = async (newPassword: string) => {
    if (!passwordResetTarget) return;

    try {
      const endpoint = passwordResetTarget.type === 'client' 
        ? `/clients/${passwordResetTarget.id}/reset-password` 
        : `/vehicles/${passwordResetTarget.id}/reset-password`;
      
      await api.post(endpoint, { new_password: newPassword });
      alert(t('password_reset_success'));
    } catch (error) {
      console.error('Error resetting password:', error);
      alert(t('password_reset_error'));
    } finally {
      setIsPasswordResetModalOpen(false);
      setPasswordResetTarget(null);
    }
  };

  const formatDate = (value: string) => {
    return new Date(value).toLocaleDateString();
  };

  const handleViewVehicle = (id: number) => {
    navigate(`/vehicles/${id}`);
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-100">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6">
            <div className="flex items-center space-x-4 mb-4">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
            <div className="space-y-4">
              {[...Array(6)].map((_, index) => (
                <Skeleton key={index} className="h-4 w-full" />
              ))}
            </div>
            <Skeleton className="h-64 w-full mt-6" />
          </div>
        </div>
      </div>
    );
  }

  if (!company) {
    return <div className="text-center py-4">{t('company_not_found')}</div>;
  }

  return (
    <div className="p-6 bg-gray-100">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">{t('company_details')}</h2>
        </div>
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 md:flex">
            <div className="md:w-1/2 md:pr-4">
              <div className="flex items-center space-x-4 mb-4">
                {company.avatar ? (
                  <img
                    src={`${process.env.REACT_APP_PIC_STORAGE}/${company.avatar}`}
                    alt={`${company.raison_social} logo`}
                    className="h-16 w-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-2xl font-bold text-gray-500">
                      {company.raison_social.charAt(0)}
                    </span>
                  </div>
                )}
                <h3 className="text-2xl font-semibold text-gray-800">{company.raison_social}</h3>
              </div>
              <div className="space-y-2 ">
                {[
                  { label: 'email', value: company.email },
                  { label: 'phone', value: company.phone },
                  { label: 'address', value: company.address },
                  { label: 'ice', value: company.ice },
                  { label: 'rib', value: company.rib },
                  { label: 'client_type', value: company.client_type }
                ].map(({ label, value }) => (
                  <p key={label} className="flex">
                    <span className="font-medium text-gray-600 w-1/4">{t(label)}:</span>
                    <span className="text-gray-800 w-3/4 text-sm">{value}</span>
                  </p>
                ))}
              </div>
            </div>
            <div className="md:w-1/2 mt-6 md:mt-0 border rounded-lg ">
              <div 
                ref={mapContainer} 
                className="w-full h-64 md:h-full rounded-lg overflow-hidden shadow-sm"
              ></div>
            </div>
          </div>
        </div>
        <div className="flex p-4 border-t border-gray-200">
          <button
            onClick={() => setActiveTab('clients')}
            className={`px-4 py-2 rounded-md ${activeTab === 'clients' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            {t('clients')}
          </button>
          <button
            onClick={() => setActiveTab('serviceOrders')}
            className={`ml-2 px-4 py-2 rounded-md ${activeTab === 'serviceOrders' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            {t('service_orders')}
          </button>
          <button
            onClick={() => setActiveTab('vehicles')}
            className={`ml-2 px-4 py-2 rounded-md ${activeTab === 'vehicles' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            {t('vehicles')}
          </button>
        </div>

        {activeTab === 'clients' && (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('first_name')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('last_name')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('email')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('phone')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {clients.map((client) => (
                    <tr key={client.id}>
                      <td className="px-6 py-4 whitespace-nowrap">{client.first_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{client.last_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{client.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{client.phone}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleResetPassword('client', client.id)}
                          className="text-blue-600 hover:text-blue-800"
                          title={t('reset_password')}
                        >
                          <Key size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
              <div>
                <p className="text-sm text-gray-700">
                  {t('showing')} <span className="font-medium">{(clientPagination.currentPage - 1) * clientPagination.perPage + 1}</span> {t('to')} <span className="font-medium">{Math.min(clientPagination.currentPage * clientPagination.perPage, clientPagination.total)}</span> {t('of')}{' '}
                  <span className="font-medium">{clientPagination.total}</span> {t('results')}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(1, 'clients')}
                    disabled={clientPagination.currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">{t('first')}</span>
                    <ChevronsLeft className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => handlePageChange(clientPagination.currentPage - 1, 'clients')}
                    disabled={clientPagination.currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">{t('previous')}</span>
                    <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    {t('page')} {clientPagination.currentPage} {t('of')} {clientPagination.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(clientPagination.currentPage + 1, 'clients')}
                    disabled={clientPagination.currentPage === clientPagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">{t('next')}</span>
                    <ChevronRight className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => handlePageChange(clientPagination.totalPages, 'clients')}
                    disabled={clientPagination.currentPage === clientPagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">{t('last')}</span>
                    <ChevronsRight className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </>
        )}

        {activeTab === 'serviceOrders' && (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('order_id')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('status')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('start_date')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('end_date')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('final_price')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('details')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {serviceOrders.map((order) => (
                    <tr key={order.id}>
                      <td className="px-6 py-4 whitespace-nowrap">{order.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          order.status === 'completed' ? 'bg-green-200 text-green-800' :
                          order.status === 'in_progress' ? 'bg-blue-200 text-blue-800' :
                          order.status === 'cancelled' ? 'bg-red-200 text-red-800' :
                          'bg-yellow-200 text-yellow-800'
                        }`}>
                          {t(order.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{formatDate(order.scheduled_at || '')}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{order.completed_at ? formatDate(order.completed_at) : ''}{order.cancelled_at ? formatDate(order.cancelled_at) : ''}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{order.final_price}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/orders/${order.id}`} className="text-blue-600 hover:text-blue-800">
                          <Eye size={18} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
              <div>
                <p className="text-sm text-gray-700">
                  {t('showing')} <span className="font-medium">{(serviceOrderPagination.currentPage - 1) * serviceOrderPagination.perPage + 1}</span> {t('to')} <span className="font-medium">{Math.min(serviceOrderPagination.currentPage * serviceOrderPagination.perPage, serviceOrderPagination.total)}</span> {t('of')}{' '}
                  <span className="font-medium">{serviceOrderPagination.total}</span> {t('results')}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(1, 'serviceOrders')}
                    disabled={serviceOrderPagination.currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">{t('first')}</span>
                    <ChevronsLeft className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => handlePageChange(serviceOrderPagination.currentPage - 1, 'serviceOrders')}
                    disabled={serviceOrderPagination.currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">{t('previous')}</span>
                    <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    {t('page')} {serviceOrderPagination.currentPage} {t('of')} {serviceOrderPagination.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(serviceOrderPagination.currentPage + 1, 'serviceOrders')}
                    disabled={serviceOrderPagination.currentPage === serviceOrderPagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">{t('next')}</span>
                    <ChevronRight className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => handlePageChange(serviceOrderPagination.totalPages, 'serviceOrders')}
                    disabled={serviceOrderPagination.currentPage === serviceOrderPagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">{t('last')}</span>
                    <ChevronsRight className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </>
        )}

        {activeTab === 'vehicles' && (
          <>
          <div className='flex p-4 border-t justify-end  border-gray-200'> 

          <button
          onClick={() => setIsAddVehicleModalOpen(true)}
          className="bg-blue-600 text-white px-4 flex items-center  py-2 rounded-md hover:bg-blue-700"
        >
          <Plus className='my-auto' size={20} />
        {t('add_vehicle')}
        </button>
        </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('plate_number')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('brand')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('model')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('year')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('fuel_type')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vehicles.map((vehicle) => (
                    <tr key={vehicle.id}>
                      <td className="px-6 py-4 whitespace-nowrap">{vehicle.plate_number}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{vehicle.brand_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{vehicle.model}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{vehicle.year}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{vehicle.fuel_type}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleResetPassword('vehicle', vehicle.id)}
                          className="text-blue-600 hover:text-blue-800 mr-2"
                          title={t('reset_password')}
                        >
                          <Key size={18} />
                        </button>
                        <button
                          onClick={() => handleViewVehicle(vehicle.id)}
                          className="text-blue-600 hover:text-blue-800 mr-2"
                          title={t('view_vehicle')}
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => {setVehicleToDelete(vehicle); setIsDeleteModalOpen(true);}}
                          className="text-red-600 hover:text-red-800"
                          title={t('delete')}
                        >
                          <Trash size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
              <div>
                <p className="text-sm text-gray-700">
                  {t('showing')} <span className="font-medium">{(vehiclePagination.currentPage - 1) * vehiclePagination.perPage + 1}</span> {t('to')} <span className="font-medium">{Math.min(vehiclePagination.currentPage * vehiclePagination.perPage, vehiclePagination.total)}</span> {t('of')}{' '}
                  <span className="font-medium">{vehiclePagination.total}</span> {t('results')}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(1, 'vehicles')}
                    disabled={vehiclePagination.currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">{t('first')}</span>
                    <ChevronsLeft className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => handlePageChange(vehiclePagination.currentPage - 1, 'vehicles')}
                    disabled={vehiclePagination.currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">{t('previous')}</span>
                    <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    {t('page')} {vehiclePagination.currentPage} {t('of')} {vehiclePagination.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(vehiclePagination.currentPage + 1, 'vehicles')}
                    disabled={vehiclePagination.currentPage === vehiclePagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">{t('next')}</span>
                    <ChevronRight className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => handlePageChange(vehiclePagination.totalPages, 'vehicles')}
                    disabled={vehiclePagination.currentPage === vehiclePagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">{t('last')}</span>
                    <ChevronsRight className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </>
        )}
      </div>

      <AddVehicleModal
        isOpen={isAddVehicleModalOpen}
        onClose={() => setIsAddVehicleModalOpen(false)}
        onSubmit={() => loadVehicles()} // Refresh vehicle list
        companyId={id}
      />
      <PasswordResetModal
        isOpen={isPasswordResetModalOpen}
        onClose={() => setIsPasswordResetModalOpen(false)}
        onConfirm={handlePasswordResetConfirm}
      />
      <DeleteVehicleModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onDelete={()=>{loadVehicles()}}
        companyId={id}
        vehicleId={vehicleToDelete?.id}
        vehiclePlateNumber={vehicleToDelete?.plate_number}
      />
    </div>
  );
}

